import { API_BASE, authenticatedFetch } from './base'

/**
 * Bug reports — user-filed issues with optional screenshot attachments.
 *
 * Backend: `backend/api/v1/bug_reports.py` (mounted at `/api/v1/bug-reports`).
 * POSTs as multipart/form-data so images ride alongside the text fields;
 * never set Content-Type manually here — the browser must generate the
 * multipart boundary itself.
 */

export interface BugReportInput {
  title: string
  description: string
  platform: string
  images: File[]
}

export interface BugReport {
  id: number
  title: string
  description: string
  platform: string
  app_version: string | null
  created_at: string
}

export type BugReportStatus = 'open' | 'closed'

export interface BugReportRead {
  id: number
  title: string
  description: string
  platform: string | null
  app_version: string | null
  status: BugReportStatus
  created_at: string
  image_count: number
}

const BASE_URL = `${API_BASE}/v1/bug-reports`

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { detail?: unknown }
    if (typeof payload.detail === 'string' && payload.detail.trim().length > 0) {
      return payload.detail
    }
  } catch {
    // Body wasn't JSON — fall through to the fallback message below.
  }
  return `${fallback} (${response.status} ${response.statusText})`
}

export async function createBugReport(input: BugReportInput): Promise<BugReport> {
  const formData = new FormData()
  formData.append('title', input.title)
  formData.append('description', input.description)
  formData.append('platform', input.platform)
  for (const image of input.images) {
    formData.append('images', image)
  }

  const response = await authenticatedFetch(BASE_URL, {
    method: 'POST',
    body: formData,
  })
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to submit bug report'))
  }
  return response.json() as Promise<BugReport>
}

/** Thrown by getAllBugReports / updateBugReportStatus on a 403 — the caller lacks the dev role. */
export class BugReportForbiddenError extends Error {
  constructor() {
    super('You do not have access to bug triage.')
    this.name = 'BugReportForbiddenError'
  }
}

/** Every user's bug reports, newest first. dev+ only — throws BugReportForbiddenError for members. */
export async function getAllBugReports(): Promise<BugReportRead[]> {
  const response = await authenticatedFetch(`${BASE_URL}/all`)
  if (response.status === 403) {
    throw new BugReportForbiddenError()
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load bug reports'))
  }
  return response.json() as Promise<BugReportRead[]>
}

/** Update a report's triage status. dev+ only. */
export async function updateBugReportStatus(
  id: number,
  status: BugReportStatus
): Promise<BugReportRead> {
  const response = await authenticatedFetch(`${BASE_URL}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to update bug report'))
  }
  return response.json() as Promise<BugReportRead>
}

/**
 * Fetch one screenshot as a Blob. Plain `<img src>` can't carry the bearer
 * token, so callers must render this via `URL.createObjectURL` and revoke
 * that URL on unmount / when the report changes.
 */
export async function getBugReportImage(id: number, position: number): Promise<Blob> {
  const response = await authenticatedFetch(`${BASE_URL}/${id}/image/${position}`)
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load image'))
  }
  return response.blob()
}
